'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import {
  ContactAttachments,
  useContactAttachments,
} from '@eliadmin/core/src/components/dlz/contact/contact-attachments';
import { CONTACT_COPY } from '@eliadmin/core/src/components/dlz/contact/contact-copy';
import {
  CheckboxField,
  MessageField,
  TextField,
} from '@eliadmin/core/src/components/dlz/contact/contact-form-fields';
import {
  CONTACT_DEFAULT_VALUES,
  SERVER_FIELD_MAP,
  contactFormSchema,
  toContactMessage,
  type ContactFormInput,
  type ContactFormValues,
} from '@eliadmin/core/src/components/dlz/contact/contact-schema';
import { ContactSent } from '@eliadmin/core/src/components/dlz/contact/contact-sent';
import { RichText } from '@eliadmin/core/src/components/dlz/ui/rich-text';
import Link from '@eliadmin/core/src/components/link';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type ContactFormProps = {
  /** HTML next to the submit button. */
  responseNote: string;
  /** HTML of the sent state with `{email}`. */
  sentText: string;
  className?: string;
};

/**
 * Figma „Contact form“ 60338:23837 (phone 60438:80688) and its sent state 60333:28411 – the left part
 * of the contact card. The form keeps the core contact form's request (`sendContactMessage` with
 * reCAPTCHA, optional secret-file attachments when the template settings allow them), its analytics
 * event and error handling; a sent message shows the thank-you state instead of the core toast and
 * redirect.
 */
export function ContactForm({ responseNote, sentText, className }: ContactFormProps) {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [writingAgain, setWritingAgain] = useState(false);

  return (
    <div
      className={cn(
        // The card is narrow next to the side column below `xl`: the inner layout follows its width.
        'flex flex-col px-4 pb-12 pt-4 @container md:py-6 md:pl-6 md:pr-10',
        sentTo !== null && 'justify-center',
        className,
      )}
    >
      {sentTo !== null ? (
        <ContactSent
          email={sentTo}
          text={sentText}
          onWriteAgain={() => {
            setWritingAgain(true);
            setSentTo(null);
          }}
        />
      ) : (
        <MessageForm responseNote={responseNote} autoFocus={writingAgain} onSent={setSentTo} />
      )}
    </div>
  );
}

function MessageForm({
  responseNote,
  autoFocus,
  onSent,
}: {
  responseNote: string;
  autoFocus: boolean;
  onSent: (email: string) => void;
}) {
  const { t } = useTranslations();
  const { toast } = useToast();
  const locale = useLocaleStore(state => state.currentLocale);
  const gtmId = useGlobalStore(state => state.generalLayout.marketing?.gtm_id);
  const showAttachments = useGlobalStore(
    state => !!state.templateSettings?.contact?.contact_form_attachments?.enabled,
  );
  const { formValidationMessages } = useFormValidationMessages(t);

  const attachments = useContactAttachments();
  const submitting = useRef(false);
  const titleId = useId();

  const form = useForm<ContactFormInput, unknown, ContactFormValues>({
    resolver: zodResolver(contactFormSchema(formValidationMessages)),
    defaultValues: CONTACT_DEFAULT_VALUES,
  });
  const { errors, isSubmitting } = form.formState;
  const formError = errors['g-recaptcha-response']?.message ?? errors.root?.message;

  useEffect(() => {
    if (autoFocus) form.setFocus('fullName');
  }, [autoFocus, form]);

  const showServerErrors = (error: ValidationError) => {
    const unmapped: string[] = [];
    let first: FieldPath<ContactFormInput> | undefined;
    for (const [field, messages] of Object.entries(error.fields)) {
      const message = messages.join('\n');
      const target = SERVER_FIELD_MAP[field];
      if (target) {
        form.setError(target, { type: 'server', message });
        first ??= target;
      } else if (field.startsWith('attachments')) {
        attachments.setError(message);
      } else {
        unmapped.push(message);
      }
    }
    if (first && first !== 'g-recaptcha-response') form.setFocus(first);
    if (unmapped.length) {
      toast({ title: t('text657'), description: unmapped.join(' '), variant: 'destructive' });
    }
  };

  const submit = form.handleSubmit(async values => {
    try {
      const uploaded = showAttachments ? await attachments.upload() : [];
      if (!uploaded) return;
      const captcha = '';
      const data = toContactMessage(values, uploaded, undefined);

      await sendContactMessage({ data, locale });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: window.location.href,
        form_length: Object.keys(data).length,
        send_to: gtmId || '',
      });
      onSent(values.email);
    } catch (error) {
      if (error instanceof ValidationError) {
        showServerErrors(error);
        return;
      }
      toast({
        title: t('text657'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <form
        noValidate
        aria-labelledby={titleId}
        onSubmit={event => {
          event.preventDefault();
          // Enter in a field while the request runs must not send the message twice.
          if (submitting.current) return;
          submitting.current = true;
          void submit(event)
            .catch(() => form.setError('root', { type: 'runtime', message: t('text657') }))
            .finally(() => {
              submitting.current = false;
            });
        }}
        className={cn(
          'flex flex-1 flex-col justify-between gap-6',
          // The first invalid field takes the focus – keep its label clear of the sticky header.
          '[&_button]:scroll-mt-20 [&_input]:scroll-mt-20 [&_textarea]:scroll-mt-20',
          'lg:[&_button]:scroll-mt-28 lg:[&_input]:scroll-mt-28 lg:[&_textarea]:scroll-mt-28',
        )}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2 @[36rem]:flex-row @[36rem]:items-center @[36rem]:justify-between @[36rem]:gap-6">
            <h2 id={titleId} className="dlz-h2 text-[#0F1012]">
              {CONTACT_COPY.title}
            </h2>
            <p className="dlz-body-s text-[#4F545C]">{CONTACT_COPY.requiredNote}</p>
          </div>
          <div className="grid gap-4 @[28rem]:grid-cols-2">
            <TextField
              name="fullName"
              label={t('text1250')}
              required
              autoComplete="name"
              placeholder={CONTACT_COPY.placeholders.fullName}
            />
            <TextField
              name="email"
              label={t('text35')}
              required
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={CONTACT_COPY.placeholders.email}
            />
            <TextField
              name="phone"
              label={t('text36')}
              type="tel"
              autoComplete="tel"
              placeholder={CONTACT_COPY.placeholders.phone}
            />
            <TextField
              name="order_number"
              label={t('text108')}
              hint={CONTACT_COPY.orderNumberHint}
              placeholder={CONTACT_COPY.placeholders.orderNumber}
            />
            <MessageField label={t('text615')} className="@[28rem]:col-span-2" />
          </div>
          {showAttachments ? (
            <ContactAttachments
              files={attachments.files}
              error={attachments.error}
              select={attachments.select}
              disabled={isSubmitting}
            />
          ) : null}
          <CheckboxField name="gdpr" required>
            {CONTACT_COPY.consent.prefix}
            <Link
              href={getDefaultUrlForTosOrGdprLocalized(ROUTES.GDPR?.() ?? '/', locale, 'gdpr')}
              target="_blank"
              prefetch={false}
              className="rounded-sm text-[#D7191F] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
            >
              {CONTACT_COPY.consent.link}
            </Link>
            {CONTACT_COPY.consent.suffix}
          </CheckboxField>
          {formError ? (
            <p role="alert" className="dlz-caption whitespace-pre-line text-[#D7191F]">
              {formError}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col-reverse gap-6 @[28rem]:flex-row @[28rem]:items-center @[28rem]:justify-between">
          <RichText
            html={responseNote}
            variant="dlz-body-s"
            className="text-[#4F545C] @[28rem]:max-w-[289px]"
          />
          <Button
            type="submit"
            isLoading={isSubmitting}
            className="h-12 w-full @[28rem]:ml-auto @[28rem]:w-auto md:h-[52px]"
          >
            {t('text2343')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
